import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { fontFamily } from '@mui/system';

const theme = createTheme({
  spacing: 4, // reduce default spacing
  palette: {
    primary: {
      main: '#0077B6',       // Healthcare teal-blue (Epic/Cerner standard)
      light: '#90E0EF',
      dark: '#005F8A',
      contrastText: '#fff',
    },
    secondary: {
      main: '#00B4D8',       // Complementary teal
      light: '#CAF0F8',
      dark: '#0096C7',
      contrastText: '#fff',
    },
    error: {
      main: '#D32F2F',
      light: '#EF5350',
      dark: '#C62828',
      contrastText: '#fff',
    },
    warning: {
      main: '#ED6C02',
      light: '#FF9800',
      dark: '#E65100',
      contrastText: '#fff',
    },
    info: {
      main: '#0288D1',
      light: '#29B6F6',
      dark: '#01579B',
      contrastText: '#fff',
    },
    success: {
      main: '#2E7D32',       // Clinical green
      light: '#4CAF50',
      dark: '#1B5E20',
      contrastText: '#fff',
    },
    background: {
      default: '#F4F6F9',    // Very light blue-gray — healthcare standard
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A2B45',    // Deep navy for readability
      secondary: '#5C6F85',  // Slate gray
      disabled: '#A0AEC0',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily: '"Inter"',
    h1: {
      fontSize: '1rem',
      '@media (min-width:900px)': {
        fontSize: '1rem',
      },
      fontWeight: 600,
      lineHeight: 1.2,
      margin: 0,
      color: '#1A2B45',
      letterSpacing: '-0.03em',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 700,
      lineHeight: 1.3,
      margin: 0,
      color: '#1A2B45',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      margin: 0,
      color: '#1A2B45',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      margin: 0,
      color: '#1A2B45',
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
      margin: 0,
      color: '#1A2B45',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.6,
      margin: 0,
      color: '#1A2B45',
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      margin: 0,
      color: '#1A2B45',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      margin: 0,
      color: '#5C6F85',
    },
    subtitle1: {
      fontSize: '0.9375rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#1A2B45',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: '#5C6F85',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.4,
      color: '#8899A8',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body': {
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
          backgroundColor: '#F4F6F9',
        },
        '.material-symbols-rounded': {
          fontFamily: "'Material Symbols Rounded'",
          fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
          fontWeight: 'normal',
          fontStyle: 'normal',
          fontSize: '24px',
          display: 'inline-block',
          lineHeight: 1,
          textTransform: 'none',
          letterSpacing: 'normal',
          wordWrap: 'normal',
          whiteSpace: 'nowrap',
          direction: 'ltr',
          WebkitFontSmoothing: 'antialiased',
        },
        '*, *::before, *::after': {
          boxSizing: 'inherit',
          margin: 0,
          padding: 0,
        },
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: '#F1F5F9',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#CBD5E1',
          borderRadius: '3px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#94A3B8',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 40,
          minWidth: 88,
          margin: 0,
          letterSpacing: '0.01em',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 119, 182, 0.25)',
          },
        },
        contained: {
          background: '#0077B6',
          '&:hover': {
            background: '#005F8A',
            boxShadow: '0 4px 12px rgba(0, 119, 182, 0.3)',
          },
        },
        outlined: {
          borderColor: '#CBD5E1',
          color: '#1A2B45',
          '&:hover': {
            borderColor: '#0077B6',
            backgroundColor: 'rgba(0, 119, 182, 0.04)',
            boxShadow: 'none',
          },
        },
        text: {
          color: '#0077B6',
          '&:hover': {
            backgroundColor: 'rgba(0, 119, 182, 0.06)',
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
          border: '1px solid #E2E8F0',
          margin: 0,
          transition: 'box-shadow 0.2s ease',
          [theme.breakpoints.down('sm')]: {
            padding: '8px',
          },
        }),
      },
      defaultProps: {
        elevation: 0,
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          margin: 0,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
        },
        outlined: {
          border: '1px solid #E2E8F0',
          boxShadow: 'none',
        },
      },
      defaultProps: {
        elevation: 0,
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 0 #E2E8F0',
          backgroundColor: '#FFFFFF',
          color: '#1A2B45',
          margin: 0,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E2E8F0',
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#CBD5E1',
            },
            '&:hover fieldset': {
              borderColor: '#94A3B8',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#0077B6',
              borderWidth: '1.5px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#5C6F85',
            '&.Mui-focused': {
              color: '#0077B6',
            },
          },
          '& .MuiInputBase-input': {
            color: '#1A2B45',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#CBD5E1',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        colorPrimary: {
          backgroundColor: 'rgba(0, 119, 182, 0.1)',
          color: '#0077B6',
        },
        colorSuccess: {
          backgroundColor: 'rgba(46, 125, 50, 0.1)',
          color: '#2E7D32',
        },
        colorWarning: {
          backgroundColor: 'rgba(237, 108, 2, 0.1)',
          color: '#ED6C02',
        },
        colorError: {
          backgroundColor: 'rgba(211, 47, 47, 0.1)',
          color: '#D32F2F',
        },
        colorInfo: {
          backgroundColor: 'rgba(2, 136, 209, 0.1)',
          color: '#0288D1',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9rem',
          color: '#5C6F85',
          minHeight: 44,
          '&.Mui-selected': {
            color: '#0077B6',
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#0077B6',
          height: 2,
          borderRadius: '2px 2px 0 0',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F8FAFC',
            color: "rgb(80, 80, 81) !important",
            fontWeight: "bold",
            fontSize: '0.8125rem',
            textTransform: 'capitalize',
            letterSpacing: '0.05em',
            borderBottom: '1px solid #E2E8F0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: '1px solid #F1F5F9',
          color: '#1A2B45',
          fontSize: '0.8rem',
          padding: '12px 16px',
          [theme.breakpoints.down('sm')]: {
            padding: '8px 10px',
            fontSize: '0.8rem',
          },
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F8FAFC',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
        },
        standardInfo: {
          backgroundColor: 'rgba(2, 136, 209, 0.08)',
          color: '#01579B',
        },
        standardSuccess: {
          backgroundColor: 'rgba(46, 125, 50, 0.08)',
          color: '#1B5E20',
        },
        standardWarning: {
          backgroundColor: 'rgba(237, 108, 2, 0.08)',
          color: '#E65100',
        },
        standardError: {
          backgroundColor: 'rgba(211, 47, 47, 0.08)',
          color: '#C62828',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E2E8F0',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: '16px !important',
          paddingRight: '16px !important',
          margin: '0 auto',
          [theme.breakpoints.down('sm')]: {
            paddingLeft: '8px !important',
            paddingRight: '8px !important',
          },
        })
      }
    },
    MuiStack: {
      defaultProps: {
        spacing: 1,
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 119, 182, 0.08)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(0, 119, 182, 0.06)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 119, 182, 0.1)',
            color: '#0077B6',
            '&:hover': {
              backgroundColor: 'rgba(0, 119, 182, 0.14)',
            },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.7rem',
          fontWeight: 700,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A2B45',
          fontSize: '0.8rem',
          borderRadius: 6,
          padding: '6px 10px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          [theme.breakpoints.down('sm')]: {
            margin: 12,
            width: 'calc(100% - 24px)',
            maxHeight: 'calc(100% - 24px)',
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 600,
          color: '#1A2B45',
          borderBottom: '1px solid #E2E8F0',
          padding: '16px 24px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '24px',
          paddingTop: '24px !important', // Ensures space for floating labels
          [theme.breakpoints.down('sm')]: {
            padding: '16px',
            paddingTop: '16px !important',
          },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: '#E2E8F0',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
  },
});

export default responsiveFontSizes(theme);